/* Midi's native-dialog implementation of Dawn's drawer lifecycle.
 * Reference: Shopify/dawn 258f00f, assets/cart-drawer.js (MIT).
 * Keep the shell mounted while replacing only server-rendered cart content.
 */
if (!customElements.get('midi-cart-drawer')) {
  customElements.define('midi-cart-drawer', class extends HTMLElement {
    connectedCallback() {
      this.ready = false;
      this.listeners?.abort();
      this.listeners = new AbortController();
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function' || !window.MidiCart || !this.querySelector('[data-cart-close]') || !this.querySelector('[data-cart-content]')) return;
      const options = { signal: this.listeners.signal };
      this.ready = true;
      this.querySelector('[data-cart-close]').addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('click', event => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.close();
      }, options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
      this.dialog.addEventListener('close', () => { if (!this.dialog.open) this.restorePage(); }, options);
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        const controls = [...this.dialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')].filter(control => control.tabIndex >= 0 && !control.disabled && control.getClientRects().length);
        const first = controls[0], last = controls.at(-1);
        if (!first) { event.preventDefault(); return; }
        if (!controls.includes(document.activeElement) || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault(); (event.shiftKey ? last : first).focus();
        }
      }, options);
      document.addEventListener('click', event => {
        const link = event.target.closest('[data-cart-open]');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(link);
      }, options);
      document.addEventListener('keydown', event => {
        const link = event.target.closest('[data-cart-open]');
        if (link && event.code === 'Space') { event.preventDefault(); this.open(link); }
      }, options);
      this.decorateTriggers();
      document.addEventListener('shopify:section:load', () => this.decorateTriggers(), options);
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.refresh({ afterCurrent: true });
      }, options);
      this.addEventListener('input', event => {
        if (event.target.matches('[data-cart-quantity]')) event.target.setCustomValidity('');
      }, options);
      this.addEventListener('change', event => {
        const input = event.target.closest('[data-cart-quantity]');
        if (!input || this.pending) return;
        const quantity = Number(input.value);
        input.setCustomValidity('');
        if (input.value === '' || !Number.isSafeInteger(quantity) || quantity < 0 || (quantity !== 0 && (quantity < Number(input.dataset.min) || quantity % Number(input.step) !== 0 || (input.hasAttribute('max') && quantity > Number(input.max))))) {
          input.setCustomValidity(this.dataset.invalid);
          input.reportValidity();
          return;
        }
        this.change(input.dataset.lineKey, quantity);
      }, options);
      this.addEventListener('click', event => {
        const remove = event.target.closest('[data-cart-remove]');
        if (!remove || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (!this.pending && !window.MidiCart.busy && !this.hasAttribute('data-cart-stale')) this.change(remove.dataset.lineKey, 0);
      }, options);
      this.querySelector('[data-cart-retry]').addEventListener('click', () => this.refresh(), options);
      this.addEventListener('submit', event => {
        if (event.submitter?.name === 'checkout') return;
        event.preventDefault();
        const input = document.activeElement;
        if (input?.matches('[data-cart-quantity]') && input.value !== input.defaultValue) input.dispatchEvent(new Event('change', { bubbles: true }));
      }, options);
      document.addEventListener('cart:updated', event => {
        if (!event.detail?.renderingSections) this.refresh();
      }, options);
      this.setAttribute('data-cart-enhanced', '');
    }

    decorateTriggers() {
      document.querySelectorAll('[data-cart-open]').forEach(link => {
        link.setAttribute('role', 'button');
        link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('aria-controls', this.dialog.id);
      });
    }

    open(trigger, { refresh = true } = {}) {
      if (!this.ready || !this.isConnected) return false;
      if (this.dialog.open) return true;
      const detail = { returnFocus: trigger || document.activeElement };
      document.dispatchEvent(new CustomEvent('cart:opening', { detail }));
      // Never stack a cart on an unrelated modal (for example a submitted newsletter response).
      if (document.querySelector('dialog[open]')) return false;
      this.opener = detail.returnFocus;
      this.previousOverflow = document.body.style.overflow;
      this.dialog.showModal();
      document.body.style.overflow = 'hidden';
      this.querySelector('[data-cart-close]').focus();
      if (refresh) this.refresh?.();
      return true;
    }

    close() {
      if (this.dialog?.open) { this.dialog.close(); this.restorePage(); }
    }

    feedback(message, recovery = false) {
      if (!this.isConnected || !this.ready) return;
      const status = this.querySelector('[data-cart-feedback]');
      if (!status) return;
      status.textContent = message || '';
      status.hidden = !message;
      const controls = this.querySelector('[data-cart-recovery]');
      if (controls) controls.hidden = !recovery;
    }

    async refresh({ afterCurrent = false } = {}) {
      if (this.refreshing) {
        if (!afterCurrent) return this.refreshing;
        // A read begun before checkout may resume with an aborted response.
        // Settle it under the existing queue, then read the returned page afresh.
        await this.refreshing;
        return this.refresh();
      }
      this.refreshing = this.readFresh();
      try { await this.refreshing; }
      finally { this.refreshing = null; }
    }

    async readFresh() {
      const release = await window.MidiCart.acquire();
      try {
        if (!this.isConnected) return;
        await window.MidiCart.sync(this.dataset.cartUrl);
        this.feedback('');
      } catch (_) {
        window.MidiCart.markStale(true);
        this.feedback(this.dataset.error, true);
      } finally { release(); }
    }

    async change(key, quantity) {
      if (this.pending || this.hasAttribute('data-cart-stale')) return;
      this.pending = true;
      this.feedback('');
      const release = await window.MidiCart.acquire();
      try {
        if (!this.isConnected) return;
        const { response, result: cart } = await window.MidiCart.requestJSON(this.dataset.changeUrl, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, ...window.MidiCart.sectionRequest() })
        });
        if (!response.ok || cart.errors) throw new Error(typeof cart.description === 'string' ? cart.description : typeof cart.errors === 'string' ? cart.errors : this.dataset.error);
        await window.MidiCart.sync(this.dataset.cartUrl, cart.sections, cart);
        this.feedback(this.dataset.updated);
      } catch (error) {
        // A failed/unreadable response may still have changed the cart. Only retry reads.
        try { await window.MidiCart.sync(this.dataset.cartUrl); this.feedback(error.name === 'AbortError' || error instanceof SyntaxError ? this.dataset.error : error.message || this.dataset.error); }
        catch (_) { window.MidiCart.markStale(true); this.feedback(this.dataset.error, true); }
      } finally { this.pending = false; release(); }
    }

    restorePage() {
      if (this.previousOverflow === undefined) return;
      if (this.previousOverflow !== undefined) {
        document.body.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
      const target = this.opener?.isConnected ? this.opener : document.querySelector('[data-cart-open]');
      if (!document.querySelector('dialog[open]')) target?.focus();
    }

    disconnectedCallback() {
      this.ready = false;
      this.listeners?.abort();
      this.close();
      this.restorePage();
      document.querySelectorAll('[data-cart-open]').forEach(link => {
        link.removeAttribute('role'); link.removeAttribute('aria-haspopup'); link.removeAttribute('aria-controls');
      });
    }
  });
}
